<template>
    <DsCard>
        <button
            :id="triggerId"
            type="button"
            :aria-controls="panelId"
            :aria-expanded="isOpenInternal ? 'true' : 'false'"
            class="accordion-panel-trigger"
            @click="toggle"
        >
            <DsInline fill :wrap="false" space="spacing-200">
                <DsText fill variant="body" style="display: flex; flex: 1;">
                    <slot name="header" />
                </DsText>

                <DsInline>
                    <DsIcon v-show="isOpenInternal" name="chevron-up" />
                    <DsIcon v-show="!isOpenInternal" name="chevron-down" />
                </DsInline>
            </DsInline>
        </button>

        <Transition name="collapse">
            <div
                v-show="isOpenInternal"
                :id="panelId"
                role="region"
                :aria-labelledby="triggerId"
                :style="{ '--collapse-content-height': contentHeight }"
            >
                <div ref="content">
                    <slot name="content" :close="close" />
                </div>
            </div>
        </Transition>
    </DsCard>
</template>

<script setup>
import {
    computed,
    ref,
    useTemplateRef,
    watch,
} from 'vue';
import { useResizeObserver } from '@vueuse/core';
import throttle from 'lodash/throttle';
import { v4 as uuidv4 } from 'uuid';
import {
    DsCard,
    DsIcon,
    DsInline,
    DsText,
} from '@infusionsoft/design-system';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: undefined,
    },
});

const emit = defineEmits(['update:isOpen']);

defineOptions({
    name: 'AccordionPanel',
});

const id = uuidv4();
const triggerId = `accordion-button-${id}`;
const panelId = `accordion-panel-${id}`;
const isOpenInternal = ref(false);
const contentHeight = ref(0);
const contentRef = useTemplateRef('content');

const isControlled = computed(() => props.isOpen === true || props.isOpen === false);

watch(() => props.isOpen, (value) => { isOpenInternal.value = value; }, { immediate: true });

function updateContentHeight() {
    if (!contentRef.value) return;

    contentHeight.value = `${contentRef.value.offsetHeight}px`;
}

useResizeObserver(contentRef, throttle(updateContentHeight, 500));

function toggle() {
    if (isControlled.value) {
        emit('update:isOpen', !props.isOpen);

        return;
    }

    isOpenInternal.value = !isOpenInternal.value;
}

function close() {
    if (isControlled.value) {
        emit('update:isOpen', false);

        return;
    }

    isOpenInternal.value = false;
}
</script>

<style scoped>
.accordion-panel-trigger {
    background: none;
    border: none;
    cursor: pointer;
    text-align: start;
    width: 100%;
    padding: var(--ds-spacing-200);
}

@keyframes collapse {
    0% {
        overflow: visible;
        opacity: 1;
        max-height: var(--collapse-content-height);
    }
    0.001% {
        overflow: hidden;
    }
    100% {
        overflow: hidden;
        opacity: 0;
        max-height: 0;
    }
}

.collapse-enter-active {
    animation: collapse .15s reverse;
}

.collapse-leave-active {
    animation: collapse .15s;
}
</style>
